/**
 * useJustFinishedFlash — encapsulates the "just finished" 1.2s highlight flash
 * used by PluginBlock / ForBlock / ContentBlock when an execution transitions
 * from `running` to a non-running, non-error terminal state.
 *
 * Centralizing this logic removes per-component useRef + useState + useEffect
 * triplets and makes the timing window unit-testable.
 */

import { useEffect, useRef, useState } from "react";

export type FlashExecStatus =
    | "idle"
    | "running"
    | "success"
    | "error"
    | "cached"
    | undefined;

export interface UseJustFinishedFlashOptions {
    /** Flash duration in ms. Defaults to 1200. */
    durationMs?: number;
    /** When true, suppress the flash even on running → success transition. */
    suppress?: boolean;
}

/**
 * Returns `true` for `durationMs` after `execStatus` transitions away from
 * `"running"` while `suppress` is false. The flash is cancelled if a new run
 * starts (status → "running") or the component unmounts.
 */
export function useJustFinishedFlash(
    execStatus: FlashExecStatus,
    options: UseJustFinishedFlashOptions = {},
): boolean {
    const { durationMs = 1200, suppress = false } = options;
    const prevStatusRef = useRef<FlashExecStatus>(execStatus);
    const [flashing, setFlashing] = useState(false);

    useEffect(() => {
        const prev = prevStatusRef.current;
        prevStatusRef.current = execStatus;

        if (prev === "running" && execStatus !== "running" && !suppress) {
            setFlashing(true);
            const t = setTimeout(() => setFlashing(false), durationMs);
            return () => clearTimeout(t);
        }
        if (execStatus === "running") {
            // A new run started — drop any in-flight flash immediately.
            setFlashing((f) => (f ? false : f));
        }
        return undefined;
    }, [execStatus, suppress, durationMs]);

    return flashing;
}
